import { createContext, use, useCallback, useMemo, useState, type ReactNode } from "react";
import type { BrandPath } from "~/types/api";

type BrandCreationState = {
  brandName: string;
  selectedCustomerId: string;
  imageFile: File | null;
  brandPaths: BrandPath[];
};

type BrandCreationContextType = {
  state: BrandCreationState;
  updateState: <K extends keyof BrandCreationState>(key: K, value: BrandCreationState[K]) => void;
  addBrandPath: () => void;
  updateBrandPath: (index: number, newProps: Partial<BrandPath>) => void;
  removeBrandPath: (index: number) => void;
};

const BrandCreationContext = createContext<BrandCreationContextType | undefined>(undefined);

const initialState: BrandCreationState = {
  brandName: "",
  selectedCustomerId: "",
  imageFile: null,
  brandPaths: [],
};

export const BrandCreationContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<BrandCreationState>(initialState);
  const updateState = useCallback(
    <K extends keyof BrandCreationState>(key: K, value: BrandCreationState[K]) => {
      setState((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const addBrandPath = useCallback(() => {
    setState((prev) => ({
      ...prev,
      brandPaths: [...prev.brandPaths, { path: "", includeSubFolders: false }],
    }));
  }, []);

  const updateBrandPath = useCallback((index: number, newProps: Partial<BrandPath>) => {
    setState((prev) => ({
      ...prev,
      brandPaths: prev.brandPaths.map((p, i) => (i === index ? { ...p, ...newProps } : p)),
    }));
  }, []);

  const removeBrandPath = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      brandPaths: prev.brandPaths.filter((_, i) => i !== index),
    }));
  }, []);

  const value = useMemo(
    () => ({
      state,
      updateState,
      addBrandPath,
      updateBrandPath,
      removeBrandPath,
    }),
    [state, updateState, addBrandPath, updateBrandPath, removeBrandPath]
  );

  return <BrandCreationContext.Provider value={value}>{children}</BrandCreationContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useBrandCreationContext = () => {
  const context = use(BrandCreationContext);
  if (!context) {
    throw new Error("useBrandCreationContext must be used within a BrandCreationContextProvider");
  }
  return context;
};
